import type { CSSProperties } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { canvasToScreen } from '../interaction/canvasToScreen'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'
import { Bubble } from './Bubble'
import styles from './RouteBubbleOverlay.module.css'

const BUBBLE_WIDTH = 50
const BUBBLE_HEIGHT = 56
// Parity with BubbleOverlay (selectedStation): plain 200ms opacity fade,
// no scale, no spring. Keep in sync with the .host / .visible transition
// in RouteBubbleOverlay.module.css — JS reads the constant for the
// leaving-bubble unmount timer; CSS reads its own literal for the visual
// opacity transition. Two values must match.
const FADE_MS = 200

/** Slot in the route picker — A = origin (from), B = destination (to). */
export type RouteBubbleSlot = 'A' | 'B'

export interface RouteBubbleOverlayHandle {
  /** Called each rAF tick with current transform; updates bubble screen position imperatively. */
  apply(zoom: number, panX: number, panY: number, dpr: number): void
}

interface Props {
  fromStation: SelectedStationSnapshot | null
  toStation: SelectedStationSnapshot | null
  /**
   * Resolves the bubble fill colour for a station's line id. Caller is
   * theme-aware (usually wraps `hexColorFromSchemaLineId`). May return
   * null if the schema is not yet loaded or the line id is unknown — in
   * that case the bubble falls back to a neutral dark fill so the white
   * A/B letter stays legible.
   */
  getLineColor: (lineId: string) => string | null
  /**
   * Getter for the current map transform. Called in [useLayoutEffect]
   * whenever the displayed-bubbles list changes so a newly-mounted
   * bubble is positioned BEFORE first paint — otherwise the element
   * ships untranslated for one frame (until the host's next rAF tick
   * fires `apply()`), visibly flashing at the viewport origin.
   * Must be a stable reference (wrap in `useCallback` or pass the ref
   * object itself) — it is called during layout.
   */
  getTransform: () => { zoom: number; panX: number; panY: number; dpr: number }
}

/** Fade-lifecycle state for one slot's bubble instance. */
interface InFlightBubble {
  slot: RouteBubbleSlot
  snapshot: SelectedStationSnapshot
  state: 'entering' | 'visible' | 'leaving'
}

/** Stable per-instance key. Slot is part of the key so the same station
 *  appearing as both A and B (degenerate but possible during a swap
 *  mid-fade) stays distinguishable across React reconciliation. */
function bubbleKey(slot: RouteBubbleSlot, stationId: string): string {
  return `${slot}:${stationId}`
}

/**
 * Renders the A (from) and B (to) map bubbles for the RouteCard
 * picker. Lifecycle mirrors {@link BubbleOverlay} (selectedStation): each
 * slot fades in on appear, fades out on clear, and on
 * `RouteSelectionSwapped` the old bubble fades out while the new one
 * fades in concurrently — all DOM nodes coexist for FADE_MS so both
 * legs of the animation are visible.
 *
 * Position updates imperatively via [apply] each rAF tick to avoid
 * React reconciliation while the user is panning. Mount-time position
 * is seeded by useLayoutEffect so newly-mounted bubbles don't flash at
 * (0, 0) for one frame.
 *
 * Slot identity is encoded in the React key (`${slot}:${stationId}`),
 * so changing the station id in a slot triggers a full unmount/remount
 * cycle through the lifecycle reducer below — that's what gives the
 * "removed-and-replaced" feel the spec calls for, rather than an
 * in-place position transition.
 */
export const RouteBubbleOverlay = forwardRef<RouteBubbleOverlayHandle, Props>(
  function RouteBubbleOverlay({ fromStation, toStation, getLineColor, getTransform }, ref) {
    const [bubbles, setBubbles] = useState<InFlightBubble[]>([])
    const positionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())

    // Reconcile per slot: for each of A / B independently, mark the
    // existing bubble 'leaving' if its station id no longer matches the
    // input, and add a new 'entering' bubble for the incoming station.
    // The list can grow up to 4 bubbles concurrently mid-swap (old A,
    // old B, new A, new B). After FADE_MS the leaving entries unmount.
    //
    // The `react-hooks/set-state-in-effect` rule discourages setState in
    // effects because of cascading renders. This effect is an exception
    // for the same reason as BubbleOverlay's: we need state that
    // persists across prop changes (leaving bubbles outlive the prop
    // change by FADE_MS).
    useEffect(() => {
      setBubbles((prev) => {
        const next: InFlightBubble[] = []
        const desired: ReadonlyArray<{
          slot: RouteBubbleSlot
          snapshot: SelectedStationSnapshot | null
        }> = [
          { slot: 'A', snapshot: fromStation },
          { slot: 'B', snapshot: toStation },
        ]
        const desiredIds = new Map<RouteBubbleSlot, string | null>(
          desired.map((d) => [d.slot, d.snapshot?.id ?? null]),
        )
        const slotsAlreadyVisible = new Set<RouteBubbleSlot>()
        for (const b of prev) {
          const desiredId = desiredIds.get(b.slot) ?? null
          if (b.snapshot.id === desiredId) {
            // Same station still selected for this slot — keep visible
            // (or revive a leaving bubble if the user re-selected the
            // same station mid-fade-out).
            next.push({ ...b, state: 'visible' })
            slotsAlreadyVisible.add(b.slot)
          } else {
            // Slot's station changed (or cleared) — fade old out.
            next.push({ ...b, state: 'leaving' })
          }
        }
        for (const d of desired) {
          if (d.snapshot === null) continue
          if (slotsAlreadyVisible.has(d.slot)) continue
          next.push({ slot: d.slot, snapshot: d.snapshot, state: 'entering' })
        }
        return next
      })
    }, [fromStation, toStation])

    // 'entering' → 'visible' after one rAF so the initial opacity:0
    // commits to the DOM before the transition to opacity:1 kicks in.
    // jsdom does not implement rAF — fall back to setTimeout(0) so the
    // state hop still fires under vi.useFakeTimers().
    useEffect(() => {
      const hasEntering = bubbles.some((b) => b.state === 'entering')
      if (!hasEntering) return
      const promote = () => {
        setBubbles((prev) =>
          prev.map((b) => (b.state === 'entering' ? { ...b, state: 'visible' } : b)),
        )
      }
      if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
        const rafId = window.requestAnimationFrame(promote)
        return () => {
          window.cancelAnimationFrame(rafId)
        }
      }
      const timerId = window.setTimeout(promote, 0)
      return () => {
        window.clearTimeout(timerId)
      }
    }, [bubbles])

    // Unmount 'leaving' bubbles after FADE_MS so the opacity transition
    // can visibly play out before React removes the DOM node.
    useEffect(() => {
      const hasLeaving = bubbles.some((b) => b.state === 'leaving')
      if (!hasLeaving) return
      const timer = window.setTimeout(() => {
        setBubbles((prev) => prev.filter((b) => b.state !== 'leaving'))
      }, FADE_MS)
      return () => {
        window.clearTimeout(timer)
      }
    }, [bubbles])

    const applyTransform = useCallback(
      (zoom: number, panX: number, panY: number, dpr: number) => {
        for (const b of bubbles) {
          const key = bubbleKey(b.slot, b.snapshot.id)
          const el = positionRefs.current.get(key)
          if (el === null || el === undefined) continue
          const { x, y } = canvasToScreen(b.snapshot.canvasX, b.snapshot.canvasY, panX, panY, zoom)
          const cssX = x / dpr
          const cssY = y / dpr
          // translate3d instead of left/top: compositor-only, no layout
          // invalidation per frame (Task 1.10). Host anchored at 0/0, so
          // the translate equals the old left/top values 1:1.
          el.style.transform = `translate3d(${cssX - BUBBLE_WIDTH / 2}px, ${cssY - BUBBLE_HEIGHT}px, 0)`
        }
      },
      [bubbles],
    )

    useImperativeHandle(ref, () => ({ apply: applyTransform }), [applyTransform])

    // Position newly-mounted bubbles BEFORE first paint so they don't
    // flash at (0, 0) for one frame. Runs on every bubbles change.
    useLayoutEffect(() => {
      const t = getTransform()
      applyTransform(t.zoom, t.panX, t.panY, t.dpr)
    }, [bubbles, applyTransform, getTransform])

    return (
      <>
        {bubbles.map((b) => {
          const key = bubbleKey(b.slot, b.snapshot.id)
          const isVisible = b.state === 'visible'
          const className = `${styles.host}${isVisible ? ` ${styles.visible}` : ''}`
          // Anchored at the container origin; per-frame positioning is a
          // translate3d in applyTransform (Task 1.10).
          const style: CSSProperties = { position: 'absolute', left: 0, top: 0 }
          // Per-bubble line-colour resolution. Falls back to a neutral
          // dark fill (matches the dim layer / iOS backgroundPrimary
          // dark) when the schema is not yet ready or the line id is
          // unknown — keeps the white A/B letter legible.
          const fillColor = getLineColor(b.snapshot.lineId) ?? '#1C1C1C'
          return (
            <div
              key={key}
              data-route-bubble-host=""
              data-slot={b.slot}
              data-state={b.state}
              ref={(el) => {
                positionRefs.current.set(key, el)
              }}
              className={className}
              style={style}
            >
              <Bubble fillColor={fillColor} text={b.slot} />
            </div>
          )
        })}
      </>
    )
  },
)
